import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Button } from 'new/elements/Button';
import { TextField } from 'new/elements/TextField';
import { Select } from 'new/elements/Select';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { useCreateExtMcpServerMutation } from '../../../state/aiApi';
import styles from './GatewaySection.module.scss';

interface AddExtMcpServerDialogProps {
  open: boolean;
  toolsetId: number;
  onClose: () => void;
}

type AuthType = 'none' | 'bearer';

interface CustomHeader {
  key: string;
  value: string;
}

function buildHeaders(
  authType: AuthType,
  bearerToken: string,
  customHeaders: CustomHeader[],
): Record<string, string> {
  const headers: Record<string, string> = {};
  if (authType === 'bearer' && bearerToken.trim()) {
    headers['Authorization'] = `Bearer ${bearerToken.trim()}`;
  }
  for (const h of customHeaders) {
    if (h.key.trim()) {
      headers[h.key.trim()] = h.value;
    }
  }
  return headers;
}

function buildDefaultConfig(
  name: string,
  url: string,
  authType: AuthType,
  bearerToken: string,
  customHeaders: CustomHeader[],
): string {
  const key = name.trim() || 'server';
  const headers = buildHeaders(authType, bearerToken, customHeaders);
  const config = {
    mcpServers: {
      [key]: {
        url: url.trim() || '',
        ...(Object.keys(headers).length > 0 ? { headers } : { headers: {} }),
      },
    },
  };
  return JSON.stringify(config, null, 2);
}

const EMPTY_MCP_CONFIG = buildDefaultConfig('server', '', 'none', '', []);

const AddExtMcpServerDialog: React.FC<AddExtMcpServerDialogProps> = ({
  open,
  toolsetId,
  onClose,
}) => {
  const { showSuccess } = useSnackbar();
  const [createServer, { isLoading }] = useCreateExtMcpServerMutation();

  const [serverName, setServerName] = useState('');
  const [serverDescription, setServerDescription] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [authType, setAuthType] = useState<AuthType>('none');
  const [bearerToken, setBearerToken] = useState('');
  const [customHeaders, setCustomHeaders] = useState<CustomHeader[]>([]);
  const [mcpConfigText, setMcpConfigText] = useState(EMPTY_MCP_CONFIG);
  const [configManuallyEdited, setConfigManuallyEdited] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const syncConfig = (
    name: string,
    url: string,
    auth: AuthType,
    token: string,
    headers: CustomHeader[],
  ) => {
    if (!configManuallyEdited) {
      setMcpConfigText(buildDefaultConfig(name, url, auth, token, headers));
    }
  };

  const handleClose = () => {
    setServerName('');
    setServerDescription('');
    setServerUrl('');
    setAuthType('none');
    setBearerToken('');
    setCustomHeaders([]);
    setMcpConfigText(EMPTY_MCP_CONFIG);
    setConfigManuallyEdited(false);
    setConfigError(null);
    setSubmitError(null);
    onClose();
  };

  const handleServerNameChange = (value: string) => {
    setServerName(value);
    syncConfig(value, serverUrl, authType, bearerToken, customHeaders);
  };

  const handleServerUrlChange = (value: string) => {
    setServerUrl(value);
    syncConfig(serverName, value, authType, bearerToken, customHeaders);
  };

  const handleAuthTypeChange = (value: AuthType) => {
    setAuthType(value);
    if (value === 'none') setBearerToken('');
    syncConfig(serverName, serverUrl, value, value === 'none' ? '' : bearerToken, customHeaders);
  };

  const handleBearerTokenChange = (value: string) => {
    setBearerToken(value);
    syncConfig(serverName, serverUrl, authType, value, customHeaders);
  };

  const handleAddCustomHeader = () => {
    const updated = [...customHeaders, { key: '', value: '' }];
    setCustomHeaders(updated);
    syncConfig(serverName, serverUrl, authType, bearerToken, updated);
  };

  const handleCustomHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
    const updated = customHeaders.map((h, i) => (i === index ? { ...h, [field]: value } : h));
    setCustomHeaders(updated);
    syncConfig(serverName, serverUrl, authType, bearerToken, updated);
  };

  const handleRemoveCustomHeader = (index: number) => {
    const updated = customHeaders.filter((_, i) => i !== index);
    setCustomHeaders(updated);
    syncConfig(serverName, serverUrl, authType, bearerToken, updated);
  };

  const handleMcpConfigChange = (value: string) => {
    setMcpConfigText(value);
    setConfigManuallyEdited(true);
    try {
      JSON.parse(value);
      setConfigError(null);
    } catch {
      setConfigError('Invalid JSON');
    }
  };

  const handleSubmit = async () => {
    if (!serverName.trim()) return;

    let parsedConfig: Record<string, any>;
    try {
      parsedConfig = JSON.parse(mcpConfigText);
    } catch {
      setConfigError('Invalid JSON — please fix before saving');
      return;
    }

    setSubmitError(null);
    try {
      await createServer({
        toolsetId,
        data: {
          server_name: serverName.trim(),
          server_description: serverDescription.trim() || undefined,
          server_url: serverUrl.trim() || undefined,
          mcp_config: parsedConfig,
        },
      }).unwrap();
      showSuccess('MCP server added');
      handleClose();
    } catch (err: any) {
      setSubmitError(err?.data?.message || 'Failed to add server');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        px={3}
        pt={2.5}
        pb={1}
      >
        <Typography variant="h6">Add External MCP Server</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2.5}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <TextField
            label="Server Name"
            value={serverName}
            onChange={(e) => handleServerNameChange(e.target.value)}
            required
            placeholder="e.g. linear-mcp"
            fullWidth
          />

          <TextField
            label="Description"
            value={serverDescription}
            onChange={(e) => setServerDescription(e.target.value)}
            placeholder="Optional description"
            fullWidth
            multiline
            rows={2}
          />

          <TextField
            label="Server URL"
            value={serverUrl}
            onChange={(e) => handleServerUrlChange(e.target.value)}
            placeholder="https://..."
            fullWidth
          />

          {/* Authentication */}
          <div>
            <Stack spacing={2}>
              <Select
                label="Authentication Type"
                value={authType}
                fullWidth
                onChange={(e) => handleAuthTypeChange(e.target.value as AuthType)}
                items={[
                  { value: 'none', title: 'No Authentication' },
                  { value: 'bearer', title: 'Bearer Token' },
                ]}
              />

              {authType === 'bearer' && (
                <TextField
                  label="Bearer Token"
                  value={bearerToken}
                  onChange={(e) => handleBearerTokenChange(e.target.value)}
                  placeholder="Enter token..."
                  fullWidth
                  type="password"
                />
              )}
            </Stack>
          </div>

          {/* Custom Headers */}
          <div>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight={500}>
                Custom Headers
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddCustomHeader}
              >
                Add Header
              </Button>
            </Stack>

            {customHeaders.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No custom headers. Click "Add Header" to include additional headers.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {customHeaders.map((header, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <TextField
                      placeholder="Header name"
                      value={header.key}
                      onChange={(e) => handleCustomHeaderChange(index, 'key', e.target.value)}
                      fullWidth
                    />
                    <TextField
                      placeholder="Value"
                      value={header.value}
                      onChange={(e) => handleCustomHeaderChange(index, 'value', e.target.value)}
                      fullWidth
                    />
                    <IconButton size="small" onClick={() => handleRemoveCustomHeader(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </div>

          <Divider />

          {/* MCP Config JSON */}
          <div>
            <Typography variant="body2" fontWeight={500} mb={0.75}>
              MCP Configuration
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Auto-generated from the fields above. Edit manually to override.
            </Typography>
            <textarea
              value={mcpConfigText}
              onChange={(e) => handleMcpConfigChange(e.target.value)}
              className={styles.jsonTextarea}
              rows={8}
              spellCheck={false}
            />
            {configError && (
              <Typography variant="caption" color="error" display="block" mt={0.5}>
                {configError}
              </Typography>
            )}
          </div>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!serverName.trim() || !!configError || isLoading}
          startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Add Server
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExtMcpServerDialog;
